import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Calendar, Gauge, Clock, CheckCircle, AlertCircle, Plus } from 'lucide-react';

// Default intervals for reference
const DEFAULT_INTERVALS = {
  oil_change: { miles: 5000, months: 6 },
  tires: { miles: 50000, months: 60 },
  brakes: { miles: 50000, months: 48 },
  windshield_wipers: { miles: 15000, months: 12 },
  battery: { miles: 50000, months: 48 },
  air_filter: { miles: 15000, months: 12 },
  registration: { miles: null, months: 12 },
  insurance: { miles: null, months: 6 },
};

const UpdateMaintenanceDialog = ({ 
  open, 
  onClose, 
  item, 
  currentMileage,
  onSubmit,
  onLogMaintenance
}) => {
  const [formData, setFormData] = useState({
    last_service_date: '',
    last_service_mileage: '',
    interval_months: '',
    interval_miles: '',
    next_due_date: '',
    next_due_mileage: '',
    custom_interval: false,
  });

  // Initialize form with existing data
  useEffect(() => {
    if (item) {
      const defaults = DEFAULT_INTERVALS[item.type] || {};
      setFormData({
        last_service_date: item.last_service_date || '',
        last_service_mileage: item.last_service_mileage || '',
        interval_months: item.interval_months || defaults.months || '',
        interval_miles: item.interval_miles || defaults.miles || '',
        next_due_date: item.next_due_date || '',
        next_due_mileage: item.next_due_mileage || '',
        custom_interval: false,
      });
    }
  }, [item]);

  // Auto-calculate next due when last service changes
  useEffect(() => {
    if (formData.last_service_date && formData.interval_months) {
      const lastDate = new Date(formData.last_service_date);
      lastDate.setMonth(lastDate.getMonth() + parseInt(formData.interval_months));
      setFormData(prev => ({
        ...prev,
        next_due_date: lastDate.toISOString().split('T')[0]
      }));
    }
  }, [formData.last_service_date, formData.interval_months]);

  useEffect(() => {
    if (formData.last_service_mileage && formData.interval_miles) {
      setFormData(prev => ({
        ...prev,
        next_due_mileage: parseInt(formData.last_service_mileage) + parseInt(formData.interval_miles)
      }));
    }
  }, [formData.last_service_mileage, formData.interval_miles]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Build submit data, converting strings to numbers where needed
    const submitData = {
      ...formData,
      last_service_mileage: formData.last_service_mileage ? parseInt(formData.last_service_mileage) : null,
      interval_months: formData.interval_months ? parseInt(formData.interval_months) : null,
      interval_miles: formData.interval_miles ? parseInt(formData.interval_miles) : null,
      next_due_mileage: formData.next_due_mileage ? parseInt(formData.next_due_mileage) : null,
    };
    
    onSubmit(submitData);
  };

  const statusConfig = {
    overdue: { label: 'Overdue', color: 'bg-red-100 text-red-700', icon: AlertCircle },
    due_soon: { label: 'Due Soon', color: 'bg-amber-100 text-amber-700', icon: Clock },
    good: { label: 'On Track', color: 'bg-green-100 text-green-700', icon: CheckCircle },
    unknown: { label: 'Ready to Set Up', color: 'bg-slate-100 text-slate-500', icon: Clock },
  };

  const config = statusConfig[item?.status] || statusConfig.unknown;
  const StatusIcon = config.icon;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <DialogTitle className="text-xl">{item?.name}</DialogTitle>
            <Badge className={config.color}>
              <StatusIcon className="h-3 w-3 mr-1" />
              {config.label}
            </Badge>
          </div>
          <p className="text-sm text-slate-500 mt-1">
            Set up or update maintenance tracking for this item
          </p>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 py-4">
          {/* Last Service Section */}
          <div className="space-y-4">
            <h4 className="font-medium text-slate-700 flex items-center gap-2">
              <Calendar className="h-4 w-4 text-teal-600" />
              Last Service
            </h4>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="last_service_date">Date</Label>
                <Input
                  id="last_service_date"
                  type="date"
                  value={formData.last_service_date}
                  onChange={(e) => handleChange('last_service_date', e.target.value)}
                  className="mt-1"
                  data-testid="last-service-date-input"
                />
              </div>
              <div>
                <Label htmlFor="last_service_mileage">Mileage</Label>
                <Input
                  id="last_service_mileage"
                  type="number"
                  placeholder={currentMileage ? `Current: ${currentMileage.toLocaleString()}` : 'Enter mileage'}
                  value={formData.last_service_mileage}
                  onChange={(e) => handleChange('last_service_mileage', e.target.value)}
                  className="mt-1"
                  data-testid="last-service-mileage-input"
                />
              </div>
            </div>
          </div>

          {/* Interval Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-medium text-slate-700 flex items-center gap-2">
                <Clock className="h-4 w-4 text-teal-600" />
                Service Interval
              </h4>
              <div className="flex items-center gap-2">
                <Label htmlFor="custom_interval" className="text-sm text-slate-500">Custom</Label>
                <Switch
                  id="custom_interval"
                  checked={formData.custom_interval}
                  onCheckedChange={(checked) => handleChange('custom_interval', checked)}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="interval_months">Every (months)</Label>
                <Input
                  id="interval_months"
                  type="number"
                  placeholder="e.g., 6"
                  value={formData.interval_months}
                  onChange={(e) => handleChange('interval_months', e.target.value)}
                  className="mt-1"
                  data-testid="interval-months-input"
                />
              </div>
              <div>
                <Label htmlFor="interval_miles">Every (miles)</Label>
                <Input
                  id="interval_miles"
                  type="number"
                  placeholder="e.g., 5000"
                  value={formData.interval_miles}
                  onChange={(e) => handleChange('interval_miles', e.target.value)}
                  className="mt-1"
                  data-testid="interval-miles-input"
                />
              </div>
            </div>
            <p className="text-xs text-slate-400">
              Default: {DEFAULT_INTERVALS[item?.type]?.months ? `${DEFAULT_INTERVALS[item?.type]?.months} months` : 'N/A'} 
              {DEFAULT_INTERVALS[item?.type]?.miles && ` / ${DEFAULT_INTERVALS[item?.type]?.miles.toLocaleString()} miles`}
            </p>
          </div>

          {/* Next Due Section (auto-calculated but editable) */}
          <div className="space-y-4">
            <h4 className="font-medium text-slate-700 flex items-center gap-2">
              <Gauge className="h-4 w-4 text-teal-600" />
              Next Due
            </h4>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="next_due_date">Date</Label>
                <Input
                  id="next_due_date"
                  type="date"
                  value={formData.next_due_date}
                  onChange={(e) => handleChange('next_due_date', e.target.value)}
                  className="mt-1"
                  data-testid="next-due-date-input"
                />
              </div>
              <div>
                <Label htmlFor="next_due_mileage">Mileage</Label>
                <Input
                  id="next_due_mileage"
                  type="number"
                  value={formData.next_due_mileage}
                  onChange={(e) => handleChange('next_due_mileage', e.target.value)}
                  className="mt-1"
                  data-testid="next-due-mileage-input"
                />
              </div>
            </div>
            <p className="text-xs text-slate-400">
              Auto-calculated from last service + interval. Adjust if needed.
            </p>
          </div>
        </form>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onLogMaintenance}
            className="flex-1 sm:flex-none"
            data-testid="log-service-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Log Service Done
          </Button>
          <div className="flex gap-2 flex-1 sm:flex-none">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              onClick={handleSubmit}
              className="bg-teal-600 hover:bg-teal-700"
              data-testid="save-maintenance-btn"
            >
              Save
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UpdateMaintenanceDialog;
